// src/pages/MessageDesktop.jsx
import React, { useMemo, useState } from "react";
import { Container, Row, Col, Card, Button, Form, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import HeaderDesktop from "../_components/_global/HeaderDesktop";
import "bootstrap-icons/font/bootstrap-icons.css";

// ---- small helper: scoped id for CSS isolation ----
function useScopeId(prefix = "msg") {
    return useMemo(() => `${prefix}-${Math.random().toString(36).slice(2, 8)}`, [prefix]);
}

// ---- Demo conversations data organized by category ----
const CONVERSATIONS_DATA = {
    personal: [
        {
            id: 1,
            name: "David Kim",
            avatar: "DK",
            role: "Fellow Traveler",
            status: "online",
            lastMessage: "Hey! Did you check out that cafe in Mile End I mentioned?",
            time: "2 min ago",
            unread: 1,
            verified: false
        },
        {
            id: 2,
            name: "Emma Wilson",
            avatar: "EW",
            role: "Friend",
            status: "away",
            lastMessage: "Thanks for the restaurant recommendation! It was amazing",
            time: "1h ago",
            unread: 0,
            verified: false
        },
        {
            id: 3,
            name: "Michael Chen",
            avatar: "MC",
            role: "Travel Buddy",
            status: "offline",
            lastMessage: "See you tomorrow at the Old Port!",
            time: "3h ago",
            unread: 0,
            verified: false
        },
        {
            id: 4,
            name: "Lisa Zhang",
            avatar: "LZ",
            role: "New Connection",
            status: "online",
            lastMessage: "Would love to join your Mount Royal hike this weekend",
            time: "Yesterday",
            unread: 0,
            verified: false
        }
    ],
    groups: [
        {
            id: 11,
            name: "Montreal Explorers",
            avatar: "ME",
            role: "128 members",
            status: "active",
            lastMessage: "John: Anyone going to the Jazz Festival tonight?",
            time: "5 min ago",
            unread: 12,
            verified: false,
            memberCount: 128
        },
        {
            id: 12,
            name: "Foodie Adventures MTL",
            avatar: "FA",
            role: "89 members",
            status: "active",
            lastMessage: "Amy: Just discovered an amazing bagel place!",
            time: "32 min ago",
            unread: 3,
            verified: false,
            memberCount: 89
        },
        {
            id: 13,
            name: "Montreal Travelers in Montreal",
            avatar: "BT",
            role: "45 members",
            status: "active",
            lastMessage: "Wei: Group dinner at Chinatown this Saturday?",
            time: "2h ago",
            unread: 7,
            verified: false,
            memberCount: 45
        },
        {
            id: 14,
            name: "Weekend Hikers Quebec",
            avatar: "WH",
            role: "234 members",
            status: "active",
            lastMessage: "Sarah: Mont Tremblant trip is confirmed for next week!",
            time: "Yesterday",
            unread: 0,
            verified: false,
            memberCount: 234
        },
        {
            id: 15,
            name: "Photography Montreal",
            avatar: "PM",
            role: "156 members",
            status: "active",
            lastMessage: "Tom: Sunset photowalk at Old Port today 6PM",
            time: "Yesterday",
            unread: 0,
            verified: false,
            memberCount: 156
        }
    ],
    guides: [
        {
            id: 21,
            name: "Sarah Chen",
            avatar: "SC",
            role: "Certified Local Guide",
            status: "online",
            lastMessage: "The Old Port walking tour starts at 2PM tomorrow",
            time: "10 min ago",
            unread: 2,
            verified: true,
            rating: 4.9,
            specialty: "Historical Tours"
        },
        {
            id: 22,
            name: "Pierre Dubois",
            avatar: "PD",
            role: "Food Tour Expert",
            status: "online",
            lastMessage: "I can show you the best poutine spots in the city!",
            time: "1h ago",
            unread: 1,
            verified: true,
            rating: 4.8,
            specialty: "Culinary Tours"
        },
        {
            id: 23,
            name: "Marie Tremblay",
            avatar: "MT",
            role: "Art & Culture Guide",
            status: "away",
            lastMessage: "Museum tour availability for this weekend confirmed",
            time: "3h ago",
            unread: 0,
            verified: true,
            rating: 5.0,
            specialty: "Museums & Galleries"
        },
        {
            id: 24,
            name: "Alex Wong",
            avatar: "AW",
            role: "Adventure Guide",
            status: "offline",
            lastMessage: "Ready for the Mount Royal sunrise hike tomorrow?",
            time: "Yesterday",
            unread: 0,
            verified: true,
            rating: 4.7,
            specialty: "Outdoor Activities"
        },
        {
            id: 25,
            name: "Jacques Leblanc",
            avatar: "JL",
            role: "Underground City Expert",
            status: "online",
            lastMessage: "Winter navigation tips for RESO underground network",
            time: "2 days ago",
            unread: 0,
            verified: true,
            rating: 4.9,
            specialty: "City Navigation"
        }
    ]
};

// ---- Demo messages for selected conversation ----
const CHAT_MESSAGES = {
    guides: [
        {
            id: 1,
            sender: "Sarah Chen",
            content: "Hi! Welcome to Montreal! I'm Sarah, your certified local guide. I specialize in historical walking tours of Old Montreal and can help you discover the city's rich 375+ year history.",
            time: "Yesterday 2:30 PM",
            isMe: false
        },
        {
            id: 2,
            sender: "Me",
            content: "Hi Sarah! I'm interested in exploring Old Montreal tomorrow. What do you recommend?",
            time: "Yesterday 3:15 PM",
            isMe: true
        },
        {
            id: 3,
            sender: "Sarah Chen",
            content: "Perfect timing! I have a small group tour tomorrow at 2PM that covers the essential historic sites. We'll visit Notre-Dame Basilica, Place Jacques-Cartier, and the Old Port area. The tour is about 2.5 hours.",
            time: "Yesterday 3:18 PM",
            isMe: false
        },
        {
            id: 4,
            sender: "Sarah Chen",
            content: "Tour highlights include:\n• Notre-Dame Basilica (interior visit)\n• Place d'Armes and its monuments\n• Saint-Paul Street art galleries\n• Bonsecours Market\n• Clock Tower viewpoint\n\nPrice: $35 CAD per person (basilica entrance included)",
            time: "Yesterday 3:20 PM",
            isMe: false,
            hasCard: true
        },
        {
            id: 5,
            sender: "Me",
            content: "That sounds great! Can I book 2 spots for the tour?",
            time: "Today 9:00 AM",
            isMe: true
        },
        {
            id: 6,
            sender: "Sarah Chen",
            content: "The Old Port walking tour starts at 2PM tomorrow. I've reserved 2 spots for you! Meeting point is at the Place d'Armes Metro station exit. Look for the blue umbrella!",
            time: "10 min ago",
            isMe: false
        }
    ],
    personal: [
        {
            id: 1,
            sender: "David Kim",
            content: "Hey! How's your Montreal trip going?",
            time: "1h ago",
            isMe: false
        },
        {
            id: 2,
            sender: "Me",
            content: "It's amazing! Just visited the Notre-Dame Basilica today",
            time: "45 min ago",
            isMe: true
        },
        {
            id: 3,
            sender: "David Kim",
            content: "Nice! Have you been to Cafe Olimpico in Mile End yet? Best coffee in the city!",
            time: "30 min ago",
            isMe: false
        },
        {
            id: 4,
            sender: "David Kim",
            content: "Hey! Did you check out that cafe in Mile End I mentioned?",
            time: "2 min ago",
            isMe: false
        }
    ],
    groups: [
        {
            id: 1,
            sender: "Amy Chen",
            content: "Hey everyone! Who's up for exploring the Jean-Talon Market this Saturday?",
            time: "2h ago",
            isMe: false
        },
        {
            id: 2,
            sender: "Mike Johnson",
            content: "Count me in! I've been wanting to check it out",
            time: "1h 45min ago",
            isMe: false
        },
        {
            id: 3,
            sender: "Lisa Wang",
            content: "Sounds fun! What time are we meeting?",
            time: "1h 30min ago",
            isMe: false
        },
        {
            id: 4,
            sender: "Me",
            content: "I'd love to join! Is 10am good for everyone?",
            time: "1h ago",
            isMe: true
        },
        {
            id: 5,
            sender: "Amy Chen",
            content: "10am works perfectly! Let's meet at the main entrance on Casgrain",
            time: "45 min ago",
            isMe: false
        },
        {
            id: 6,
            sender: "John Davis",
            content: "Anyone going to the Jazz Festival tonight?",
            time: "5 min ago",
            isMe: false
        }
    ]
};

export default function MessageDesktop() {
    const scope = useScopeId();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("personal");
    const [selectedChat, setSelectedChat] = useState(CONVERSATIONS_DATA.personal[0]);
    const [message, setMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const handleSendMessage = () => {
        if (message.trim()) {
            console.log("Sending:", message);
            setMessage("");
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectedChat(CONVERSATIONS_DATA[tab][0]);
    };

    const getMessages = () => {
        if (selectedChat.id >= 21) return CHAT_MESSAGES.guides;
        if (selectedChat.id >= 11) return CHAT_MESSAGES.groups;
        return CHAT_MESSAGES.personal;
    };

    const css = `
    [data-msg="${scope}"] {
        height: calc(100vh - 80px);
        background: #f8f9fb;
    }
    [data-msg="${scope}"] .sidebar {
        background: #fff;
        border-right: 1px solid #e8edf2;
        height: 100%;
        display: flex;
        flex-direction: column;
    }
    [data-msg="${scope}"] .chat-area {
        background: #fff;
        height: 100%;
        display: flex;
        flex-direction: column;
    }
    [data-msg="${scope}"] .tabs-container {
        padding: 12px 16px;
        border-bottom: 1px solid #e8edf2;
        background: #fff;
    }
    [data-msg="${scope}"] .tabs-row {
        display: flex;
        gap: 8px;
        background: #f6f8fb;
        padding: 4px;
        border-radius: 14px;
    }
    [data-msg="${scope}"] .tab-btn {
        flex: 1;
        padding: 10px 16px;
        border: none;
        background: transparent;
        color: #6a7a8a;
        font-weight: 600;
        font-size: 14px;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
    }
    [data-msg="${scope}"] .tab-btn:hover {
        color: #0f2538;
    }
    [data-msg="${scope}"] .tab-btn.active {
        background: #0f2538;
        color: #fff;
    }
    [data-msg="${scope}"] .tab-icon {
        font-size: 16px;
    }
    [data-msg="${scope}"] .search-box {
        padding: 12px 16px;
        border-bottom: 1px solid #e8edf2;
    }
    [data-msg="${scope}"] .search-input {
        background: #f6f8fb;
        border: 1px solid #e8edf2;
        border-radius: 12px;
        padding: 10px 16px;
        font-size: 14px;
    }
    [data-msg="${scope}"] .search-input:focus {
        background: #fff;
        border-color: #0f2538;
        box-shadow: none;
    }
    [data-msg="${scope}"] .conversations-list {
        flex: 1;
        overflow-y: auto;
        padding: 8px;
    }
    [data-msg="${scope}"] .conv-item {
        padding: 12px;
        border-radius: 14px;
        margin-bottom: 4px;
        cursor: pointer;
        transition: all 0.2s;
        border: 1px solid transparent;
    }
    [data-msg="${scope}"] .conv-item:hover {
        background: #f6f8fb;
    }
    [data-msg="${scope}"] .conv-item.active {
        background: #eef2f7;
        border-color: #e8edf2;
    }
    [data-msg="${scope}"] .avatar {
        width: 48px;
        height: 48px;
        border-radius: 14px;
        background: linear-gradient(135deg, #0f2538, #2b4e6b);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 16px;
        position: relative;
        flex-shrink: 0;
    }
    [data-msg="${scope}"] .avatar.group {
        background: linear-gradient(135deg, #7c3aed, #a855f7);
    }
    [data-msg="${scope}"] .avatar.guide {
        background: linear-gradient(135deg, #059669, #10b981);
    }
    [data-msg="${scope}"] .status-dot {
        position: absolute;
        bottom: 2px;
        right: 2px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid #fff;
    }
    [data-msg="${scope}"] .status-dot.online {
        background: #16a34a;
    }
    [data-msg="${scope}"] .status-dot.away {
        background: #f59e0b;
    }
    [data-msg="${scope}"] .status-dot.offline {
        background: #6b7280;
    }
    [data-msg="${scope}"] .status-dot.active {
        background: #3b82f6;
    }
    [data-msg="${scope}"] .conv-info {
        flex: 1;
        min-width: 0;
        margin-left: 12px;
    }
    [data-msg="${scope}"] .conv-name {
        font-weight: 700;
        color: #0f1720;
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 2px;
    }
    [data-msg="${scope}"] .verified-badge {
        color: #3b82f6;
        font-size: 14px;
    }
    [data-msg="${scope}"] .conv-role {
        font-size: 12px;
        color: #6a7a8a;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        gap: 6px;
    }
    [data-msg="${scope}"] .rating {
        color: #f59e0b;
        font-size: 12px;
        display: flex;
        align-items: center;
        gap: 2px;
    }
    [data-msg="${scope}"] .conv-last-msg {
        font-size: 13px;
        color: #6a7a8a;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    [data-msg="${scope}"] .conv-meta {
        text-align: right;
        min-width: 60px;
    }
    [data-msg="${scope}"] .conv-time {
        font-size: 12px;
        color: #6a7a8a;
        margin-bottom: 6px;
    }
    [data-msg="${scope}"] .unread-badge {
        background: #ef4444;
        color: #fff;
        border-radius: 10px;
        padding: 2px 8px;
        font-size: 11px;
        font-weight: 700;
        display: inline-block;
    }
    [data-msg="${scope}"] .chat-header {
        padding: 16px 20px;
        border-bottom: 1px solid #e8edf2;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    [data-msg="${scope}"] .chat-header-info {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    [data-msg="${scope}"] .chat-header-details {
        display: flex;
        flex-direction: column;
    }
    [data-msg="${scope}"] .chat-header-specialty {
        font-size: 12px;
        color: #6a7a8a;
        margin-top: 2px;
    }
    [data-msg="${scope}"] .chat-header-actions {
        display: flex;
        gap: 8px;
    }
    [data-msg="${scope}"] .icon-btn {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        border: 1px solid #e8edf2;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s;
        color: #6a7a8a;
    }
    [data-msg="${scope}"] .icon-btn:hover {
        background: #f6f8fb;
        color: #0f2538;
    }
    [data-msg="${scope}"] .back-btn {
        background: #0f2538;
        color: #fff;
        border: none;
        padding: 8px 16px;
        border-radius: 10px;
        font-weight: 600;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        transition: all 0.2s;
    }
    [data-msg="${scope}"] .back-btn:hover {
        background: #1a3147;
    }
    [data-msg="${scope}"] .messages-area {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        background: #fafbfc;
    }
    [data-msg="${scope}"] .message {
        margin-bottom: 16px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }
    [data-msg="${scope}"] .message.me {
        flex-direction: row-reverse;
    }
    [data-msg="${scope}"] .msg-avatar {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: linear-gradient(135deg, #0f2538, #2b4e6b);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        flex-shrink: 0;
    }
    [data-msg="${scope}"] .message.me .msg-avatar {
        background: linear-gradient(135deg, #3b82f6, #6366f1);
    }
    [data-msg="${scope}"] .msg-content {
        max-width: 65%;
    }
    [data-msg="${scope}"] .msg-bubble {
        background: #fff;
        border: 1px solid #e8edf2;
        border-radius: 16px;
        padding: 12px 16px;
        margin-bottom: 4px;
    }
    [data-msg="${scope}"] .message.me .msg-bubble {
        background: #3b82f6 !important;
        color: #ffffff !important;
        border-color: #3b82f6 !important;
    }
    [data-msg="${scope}"] .msg-text {
        font-size: 14px;
        line-height: 1.5;
        margin: 0;
        white-space: pre-wrap;
    }
    [data-msg="${scope}"] .msg-time {
        font-size: 11px;
        color: #6a7a8a;
        margin-left: 8px;
    }
    [data-msg="${scope}"] .message.me .msg-time {
        text-align: right;
        margin-right: 8px;
    }
    [data-msg="${scope}"] .recommendation-card {
        background: #f6f8fb;
        border-radius: 12px;
        padding: 12px;
        margin-top: 8px;
    }
    [data-msg="${scope}"] .rec-title {
        font-weight: 600;
        color: #0f1720;
        font-size: 14px;
        margin-bottom: 8px;
    }
    [data-msg="${scope}"] .rec-item {
        padding: 8px 0;
        border-bottom: 1px solid #e8edf2;
        font-size: 13px;
        color: #4a5568;
        line-height: 1.4;
    }
    [data-msg="${scope}"] .rec-item:last-child {
        border-bottom: none;
    }
    [data-msg="${scope}"] .message-input-area {
        padding: 16px 20px;
        border-top: 1px solid #e8edf2;
        background: #fff;
    }
    [data-msg="${scope}"] .input-wrapper {
        display: flex;
        gap: 12px;
        align-items: center;
    }
    [data-msg="${scope}"] .message-input {
        flex: 1;
        background: #f6f8fb;
        border: 1px solid #e8edf2;
        border-radius: 12px;
        padding: 12px 16px;
        font-size: 14px;
        resize: none;
        min-height: 48px;
        max-height: 120px;
    }
    [data-msg="${scope}"] .message-input:focus {
        background: #fff;
        border-color: #0f2538;
        outline: none;
    }
    [data-msg="${scope}"] .send-btn {
        background: #0f2538;
        color: #fff;
        border: none;
        border-radius: 12px;
        padding: 12px 24px;
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 6px;
    }
    [data-msg="${scope}"] .send-btn:hover {
        background: #1a3147;
    }
    [data-msg="${scope}"] .send-btn:disabled {
        background: #94a3b8;
        cursor: not-allowed;
    }
    `;

    return (
        <>
            <style>{css}</style>
            
            <HeaderDesktop
                active="messages"
                cityName="Montreal"
                unread={7}
                onSearch={(q) => navigate(`/explore?q=${encodeURIComponent(q)}`)}
            />

            <Container fluid data-msg={scope}>
                <Row className="g-0 h-100">
                    {/* Sidebar - Conversations List */}
                    <Col lg={4} xl={3} className="sidebar">
                        {/* Tabs */}
                        <div className="tabs-container">
                            <div className="tabs-row">
                                <button
                                    className={`tab-btn ${activeTab === "personal" ? "active" : ""}`}
                                    onClick={() => handleTabChange("personal")}
                                >
                                    <i className="bi bi-person tab-icon" />
                                    Personal
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === "groups" ? "active" : ""}`}
                                    onClick={() => handleTabChange("groups")}
                                >
                                    <i className="bi bi-people tab-icon" />
                                    Groups
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === "guides" ? "active" : ""}`}
                                    onClick={() => handleTabChange("guides")}
                                >
                                    <i className="bi bi-compass tab-icon" />
                                    Guides
                                </button>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="search-box">
                            <Form.Control
                                type="text"
                                placeholder={`Search ${activeTab}...`}
                                className="search-input"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        
                        {/* Conversations */}
                        <div className="conversations-list">
                            {CONVERSATIONS_DATA[activeTab].map(conv => (
                                <div
                                    key={conv.id}
                                    className={`conv-item d-flex ${selectedChat.id === conv.id ? 'active' : ''}`}
                                    onClick={() => setSelectedChat(conv)}
                                >
                                    <div className={`avatar ${activeTab === 'groups' ? 'group' : activeTab === 'guides' ? 'guide' : ''}`}>
                                        {conv.avatar}
                                        <span className={`status-dot ${conv.status}`}></span>
                                    </div>
                                    <div className="conv-info">
                                        <div className="conv-name">
                                            {conv.name}
                                            {conv.verified && <i className="bi bi-patch-check-fill verified-badge" />}
                                        </div>
                                        <div className="conv-role">
                                            {conv.role}
                                            {conv.rating && (
                                                <span className="rating">
                                                    <i className="bi bi-star-fill" />
                                                    {conv.rating}
                                                </span>
                                            )}
                                        </div>
                                        <div className="conv-last-msg">{conv.lastMessage}</div>
                                    </div>
                                    <div className="conv-meta">
                                        <div className="conv-time">{conv.time}</div>
                                        {conv.unread > 0 && (
                                            <span className="unread-badge">{conv.unread}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Col>

                    {/* Chat Area */}
                    <Col lg={8} xl={9} className="chat-area">
                        {/* Chat Header */}
                        <div className="chat-header">
                            <div className="chat-header-info">
                                <button className="back-btn" onClick={() => navigate(-1)}>
                                    <i className="bi bi-arrow-left" />
                                    Back
                                </button>
                                <div className={`avatar ${selectedChat.id >= 21 ? 'guide' : selectedChat.id >= 11 ? 'group' : ''}`}>
                                    {selectedChat.avatar}
                                    <span className={`status-dot ${selectedChat.status}`}></span>
                                </div>
                                <div className="chat-header-details">
                                    <div className="conv-name">
                                        {selectedChat.name}
                                        {selectedChat.verified && <i className="bi bi-patch-check-fill verified-badge" />}
                                    </div>
                                    <div className="conv-role">
                                        {selectedChat.role}
                                        {selectedChat.rating && (
                                            <span className="rating ms-2">
                                                <i className="bi bi-star-fill" />
                                                {selectedChat.rating}
                                            </span>
                                        )}
                                    </div>
                                    {selectedChat.specialty && (
                                        <div className="chat-header-specialty">{selectedChat.specialty}</div>
                                    )}
                                </div>
                            </div>
                            <div className="chat-header-actions">
                                <button className="icon-btn">
                                    <i className="bi bi-telephone" />
                                </button>
                                <button className="icon-btn">
                                    <i className="bi bi-camera-video" />
                                </button>
                                <button className="icon-btn">
                                    <i className="bi bi-info-circle" />
                                </button>
                                <button className="icon-btn">
                                    <i className="bi bi-three-dots-vertical" />
                                </button>
                            </div>
                        </div>

                        {/* Messages Area */}
                        <div className="messages-area">
                            {getMessages().map(msg => (
                                <div key={msg.id} className={`message ${msg.isMe ? 'me' : ''}`}>
                                    <div className="msg-avatar">
                                        {msg.isMe ? 'Me' : selectedChat.avatar.slice(0, 2)}
                                    </div>
                                    <div className="msg-content">
                                        <div className="msg-bubble">
                                            <p className="msg-text">{msg.content}</p>
                                            {msg.hasCard && (
                                                <div className="recommendation-card">
                                                    <div className="rec-title">Tour Details</div>
                                                    <div className="rec-item">
                                                        <strong>Duration:</strong> 2.5 hours walking tour
                                                    </div>
                                                    <div className="rec-item">
                                                        <strong>Meeting Point:</strong> Place d'Armes Metro
                                                    </div>
                                                    <div className="rec-item">
                                                        <strong>Price:</strong> $35 CAD per person
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="msg-time">{msg.time}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Message Input Area */}
                        <div className="message-input-area">
                            <div className="input-wrapper">
                                <button className="icon-btn">
                                    <i className="bi bi-paperclip" />
                                </button>
                                <button className="icon-btn">
                                    <i className="bi bi-image" />
                                </button>
                                <Form.Control
                                    as="textarea"
                                    className="message-input"
                                    placeholder="Type your message..."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                />
                                <button 
                                    className="send-btn" 
                                    onClick={handleSendMessage}
                                    disabled={!message.trim()}
                                >
                                    <i className="bi bi-send" />
                                    Send
                                </button>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            
        </>
    );
}